/**
 * Quiz page — optional refinement options, unit conversions, and the
 * height/weight measurement inputs.
 *
 * Extracted from ``pages/Quiz.js`` (lines 184-450). Keeping the option
 * tables and the two large stateful inputs in a sibling module lets the
 * main ``Quiz`` component focus on step orchestration and network I/O.
 */
import { useEffect, useState } from 'react';


// ============ OPTIONAL REFINEMENT STEPS (all skippable) ============
export const HERITAGE_OPTIONS = [
  { value: 'east_asian', label: 'East Asian' },
  { value: 'south_asian', label: 'South Asian' },
  { value: 'southeast_asian', label: 'Southeast Asian' },
  { value: 'middle_eastern_north_african', label: 'Middle Eastern / North African' },
  { value: 'african', label: 'Sub-Saharan African' },
  { value: 'caribbean', label: 'Caribbean' },
  { value: 'latin_hispanic', label: 'Latin / Hispanic' },
  { value: 'european', label: 'European / Western' },
  { value: 'indigenous', label: 'Indigenous' },
  { value: 'multi', label: 'Mixed Heritage' },
  { value: 'refuse', label: 'Prefer not to say' }
];

export const RELIGIOUS_EVENTS_OPTIONS = [
  { value: 'religious_worship', label: 'Religious worship attire' },
  { value: 'cultural_weddings', label: 'Cultural / religious weddings' },
  { value: 'cultural_celebrations', label: 'Cultural celebrations' },
  { value: 'formal_religious', label: 'Formal religious occasions' }
];

export const MODESTY_OPTIONS = [
  { value: 'long_sleeves', label: 'Long sleeves preferred' },
  { value: 'high_necklines', label: 'High necklines preferred' },
  { value: 'knee_length_plus', label: 'Knee-length or longer' },
  { value: 'loose_silhouettes', label: 'Loose, non-form-fitting silhouettes' },
  { value: 'head_covering', label: 'Head-covering compatible' },
  { value: 'fully_covered', label: 'Fully covered styling' }
];

export const SIZE_SYSTEMS = [
  { value: 'us', label: 'US' },
  { value: 'uk', label: 'UK' },
  { value: 'eu', label: 'EU' },
  { value: 'jp', label: 'JP' },
  { value: 'cn', label: 'CN' },
  { value: 'kr', label: 'KR' }
];

export const FABRIC_AVOID_OPTIONS = [
  { value: 'wool', label: 'Wool' },
  { value: 'leather', label: 'Leather' },
  { value: 'silk', label: 'Silk' },
  { value: 'synthetic', label: 'Synthetic fibres' },
  { value: 'fur', label: 'Fur' },
  { value: 'linen', label: 'Linen' }
];

// ============ HEIGHT / WEIGHT CONVERSIONS ============
export const cmToFeetInches = (cm) => {
  const totalInches = Math.round(cm / 2.54);
  return { feet: Math.floor(totalInches / 12), inches: totalInches % 12 };
};
export const feetInchesToCm = (feet, inches) => Math.round((feet * 12 + inches) * 2.54);
export const kgToLbs = (kg) => Math.round(kg / 0.453592);
export const lbsToKg = (lbs) => Math.round(lbs * 0.453592);

// Strip non-numeric characters (handles users typing "215lb", "97kg", etc.)
export const cleanNumeric = (raw) => String(raw ?? '').replace(/[^0-9]/g, '');

// ============ HEIGHT INPUT ============
export const HeightInput = ({ value, units, onChange }) => {
  // Local state — typed value (in user's unit). Avoids round-trip rounding drift.
  const [cm, setCm] = useState(() => String(value));
  const [feet, setFeet] = useState(() => String(cmToFeetInches(value).feet));
  const [inches, setInches] = useState(() => String(cmToFeetInches(value).inches));
  const [error, setError] = useState('');

  // Sync local state when units change (e.g., user went back & switched units)
  useEffect(() => {
    if (units === 'imperial') {
      const fi = cmToFeetInches(value);
      setFeet(String(fi.feet));
      setInches(String(fi.inches));
    } else {
      setCm(String(value));
    }
    setError('');
  }, [units]); // eslint-disable-line react-hooks/exhaustive-deps

  const MIN_CM = 120;
  const MAX_CM = 230;

  const commitCm = (newCm) => {
    if (newCm < MIN_CM) { setError(`Height must be at least ${MIN_CM} cm (${cmToFeetInches(MIN_CM).feet}'${cmToFeetInches(MIN_CM).inches}")`); return; }
    if (newCm > MAX_CM) { setError(`Height must be no more than ${MAX_CM} cm (${cmToFeetInches(MAX_CM).feet}'${cmToFeetInches(MAX_CM).inches}")`); return; }
    setError('');
    onChange(newCm);
  };

  if (units === 'imperial') {
    const handleFeet = (raw) => {
      const cleaned = cleanNumeric(raw);
      setFeet(cleaned);
      const f = parseInt(cleaned, 10);
      const i = parseInt(inches, 10) || 0;
      if (!isNaN(f)) {
        commitCm(feetInchesToCm(f, i));
      } else {
        setError('Please enter your height');
        onChange(0);
      }
    };
    const handleInches = (raw) => {
      const cleaned = cleanNumeric(raw);
      setInches(cleaned);
      const i = parseInt(cleaned, 10);
      const f = parseInt(feet, 10) || 0;
      if (!isNaN(i)) {
        if (i > 11) { setError('Inches must be 0–11. Use feet for taller heights.'); onChange(0); }
        else commitCm(feetInchesToCm(f, i));
      } else {
        if (!isNaN(f)) commitCm(feetInchesToCm(f, 0));
        else { setError('Please enter your height'); onChange(0); }
      }
    };
    return (
      <div className="mb-8">
        <div className="flex items-end justify-center gap-8">
          <div className="text-center">
            <input
              type="text" inputMode="numeric" pattern="[0-9]*"
              value={feet}
              onChange={(e) => handleFeet(e.target.value)}
              data-testid="quiz-height-feet"
              className="text-6xl font-serif text-center bg-transparent border-b border-[#E5E4E2] focus:border-[#0D0D0D] focus:outline-none w-24 py-2"
              aria-label="Feet"
            />
            <div className="font-sans uppercase tracking-[0.2em] text-xs text-[#8E8A84] mt-3">feet</div>
          </div>
          <div className="text-center">
            <input
              type="text" inputMode="numeric" pattern="[0-9]*"
              value={inches}
              onChange={(e) => handleInches(e.target.value)}
              data-testid="quiz-height-inches"
              className="text-6xl font-serif text-center bg-transparent border-b border-[#E5E4E2] focus:border-[#0D0D0D] focus:outline-none w-24 py-2"
              aria-label="Inches"
            />
            <div className="font-sans uppercase tracking-[0.2em] text-xs text-[#8E8A84] mt-3">inches</div>
          </div>
        </div>
        {error && <p className="text-center font-sans text-sm text-[#9B5A4B] mt-4" data-testid="quiz-height-error">{error}</p>}
        <p className="text-center font-sans text-xs text-[#8E8A84] mt-3">Type directly or use the field above</p>
      </div>
    );
  }

  // Metric
  const handleCm = (raw) => {
    const cleaned = cleanNumeric(raw);
    setCm(cleaned);
    if (cleaned === '') { setError('Please enter your height'); onChange(0); return; }
    const n = parseInt(cleaned, 10);
    if (!isNaN(n)) commitCm(n);
  };
  const stepCm = (delta) => {
    const current = parseInt(cm, 10) || 170;
    const next = Math.max(MIN_CM, Math.min(MAX_CM, current + delta));
    setCm(String(next));
    commitCm(next);
  };
  return (
    <div className="mb-8">
      <div className="flex items-center justify-center gap-6">
        <button onClick={() => stepCm(-1)} data-testid="quiz-height-decrease"
          className="w-12 h-12 rounded-full border border-[#0D0D0D] flex items-center justify-center hover:bg-[#0D0D0D] hover:text-white transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" /></svg>
        </button>
        <div className="text-center">
          <input
            type="text" inputMode="numeric" pattern="[0-9]*"
            value={cm}
            onChange={(e) => handleCm(e.target.value)}
            data-testid="quiz-height-cm"
            className="text-6xl font-serif text-center bg-transparent border-b border-[#E5E4E2] focus:border-[#0D0D0D] focus:outline-none w-32 py-2"
            aria-label="Height in centimetres"
          />
          <div className="font-sans uppercase tracking-[0.2em] text-xs text-[#8E8A84] mt-3">cm</div>
        </div>
        <button onClick={() => stepCm(1)} data-testid="quiz-height-increase"
          className="w-12 h-12 rounded-full border border-[#0D0D0D] flex items-center justify-center hover:bg-[#0D0D0D] hover:text-white transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
        </button>
      </div>
      {error && <p className="text-center font-sans text-sm text-[#9B5A4B] mt-4" data-testid="quiz-height-error">{error}</p>}
      <p className="text-center font-sans text-xs text-[#8E8A84] mt-3">Type directly or use ± to fine-tune</p>
    </div>
  );
};

// ============ WEIGHT INPUT ============
export const WeightInput = ({ value, units, onChange }) => {
  // Local state in the user's chosen unit. Avoids round-trip rounding drift.
  const [displayed, setDisplayed] = useState(() =>
    String(units === 'imperial' ? kgToLbs(value) : value)
  );
  const [error, setError] = useState('');

  // Re-sync only when units toggle, NOT on every value change (prevents drift)
  useEffect(() => {
    setDisplayed(String(units === 'imperial' ? kgToLbs(value) : value));
    setError('');
  }, [units]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generous, realistic ranges
  const MIN_KG = 30;
  const MAX_KG = 250;
  const MIN_LBS = kgToLbs(MIN_KG); // ~66
  const MAX_LBS = kgToLbs(MAX_KG); // ~551
  const isImperial = units === 'imperial';
  const minDisplay = isImperial ? MIN_LBS : MIN_KG;
  const maxDisplay = isImperial ? MAX_LBS : MAX_KG;
  const unitLabel = isImperial ? 'lbs' : 'kg';

  const commitNumber = (n) => {
    const kg = isImperial ? lbsToKg(n) : n;
    if (n < minDisplay) { setError(`Weight must be at least ${minDisplay} ${unitLabel}`); onChange(kg); return; }
    if (n > maxDisplay) { setError(`Weight must be no more than ${maxDisplay} ${unitLabel}`); onChange(kg); return; }
    setError('');
    onChange(kg);
  };

  const handleInput = (raw) => {
    const cleaned = cleanNumeric(raw);
    setDisplayed(cleaned);
    if (cleaned === '') { setError('Please enter your weight'); onChange(0); return; }
    const n = parseInt(cleaned, 10);
    if (!isNaN(n)) commitNumber(n);
  };

  const stepBy = (delta) => {
    const current = parseInt(displayed, 10);
    const start = isNaN(current) ? (isImperial ? 154 : 70) : current;
    const next = Math.max(minDisplay, Math.min(maxDisplay, start + delta));
    setDisplayed(String(next));
    commitNumber(next);
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-center gap-6">
        <button onClick={() => stepBy(-1)} data-testid="quiz-weight-decrease"
          className="w-12 h-12 rounded-full border border-[#0D0D0D] flex items-center justify-center hover:bg-[#0D0D0D] hover:text-white transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" /></svg>
        </button>
        <div className="text-center">
          <input
            type="text" inputMode="numeric" pattern="[0-9]*"
            value={displayed}
            onChange={(e) => handleInput(e.target.value)}
            data-testid={isImperial ? 'quiz-weight-lbs' : 'quiz-weight-kg'}
            className="text-6xl font-serif text-center bg-transparent border-b border-[#E5E4E2] focus:border-[#0D0D0D] focus:outline-none w-40 py-2"
            aria-label={`Weight in ${unitLabel}`}
          />
          <div className="font-sans uppercase tracking-[0.2em] text-xs text-[#8E8A84] mt-3">{unitLabel}</div>
        </div>
        <button onClick={() => stepBy(1)} data-testid="quiz-weight-increase"
          className="w-12 h-12 rounded-full border border-[#0D0D0D] flex items-center justify-center hover:bg-[#0D0D0D] hover:text-white transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
        </button>
      </div>
      {error && <p className="text-center font-sans text-sm text-[#9B5A4B] mt-4" data-testid="quiz-weight-error">{error}</p>}
      <p className="text-center font-sans text-xs text-[#8E8A84] mt-3">Type directly or use ± to fine-tune</p>
    </div>
  );
};
